<script setup lang="ts">
import type { EntryCollection, EntrySkeletonType } from 'contentful'
import { getSeoPanelData } from '~/helpers/seo'

const route = useRoute()
const slug = route.path.replace(/^\/fr(\/|$)/i, '/')
const { t, locale } = useI18n()

// Initialize Organization Schema (Home page only)
if (slug === '/' || slug === '') {
  useSchemaOrg().init()
}

const pageType = useState('pageType', () => 'unknown')
const { urlToCategoryId } = useSiteUrl()
const event = useRequestEvent()

const { isPreviewEnabled, previewLocale } = useContentfulPreview()
const previewStore = useContentfulPreviewStore()
let previewOrAppLocale: Locales = locale.value
if (isPreviewEnabled && previewLocale) {
  previewOrAppLocale = previewLocale === 'fr-CA' ? 'fr' : previewLocale
}

const { data, error } = await useAsyncData(`slug:${slug}`, handler, {
  transform,
  deep: false,
})
if (error.value) {
  if (event) {
    setResponseStatus(event, 404, error.value.message)
  }
}
const mappedContentType = mapContentfulContentTypeToPageContentType(data.value?.pageType?.value ?? 'unknown', slug)
const breadcrumbs = ref<{ text: string; url: string }[] | undefined>(
  data.value?.pageType?.value === 'productListingPage' && previewOrAppLocale === 'fr'
    ? [{ text: t('home_text'), url: '/fr/' }]
    : undefined,
)

async function handler() {
  const { getEntries } = useContentful()
  const entries = await Promise.all([
    getEntries({
      content_type: 'slug',
      'fields.slug': slug,
      limit: 1,
      locale: localeConfig[previewOrAppLocale].code,
    }),
    getEntries({
      content_type: 'productListingPage',
      'fields.categoryId': urlToCategoryId(slug.slice(1)),
      limit: 1,
      locale: localeConfig[previewOrAppLocale].code,
    }),
  ])

  const [slugEntries, plpEntries] = entries
  const validEntries = slugEntries?.items?.length ? slugEntries : plpEntries?.items?.length ? plpEntries : undefined

  return validEntries
}

function transform(entries: EntryCollection<EntrySkeletonType, undefined, string> | undefined) {
  if (!entries || !entries.items.length) throw new Error('Page Not Found')
  const { items, includes } = entries
  const item = items?.[0]
  if (!item) throw new Error('Page Not Found')

  const itemType = item.sys?.contentType?.sys?.id

  const localeKey = `pages_${localeConfig[previewOrAppLocale].code.replace('-', '_')}`
  let { fields }: { fields: any } = item

  if (isPreviewEnabled) previewStore.entries = entries

  if (itemType === 'slug' || (isPreviewEnabled && itemType === 'contentPage')) {
    // Grab locale specific data from pages_en_AU: {}, pages_en_CA: {} ...
    // When no fr-CA page, take en-CA page instead
    const pages: any = fields[localeKey] || (previewOrAppLocale === 'fr' && fields['pages_en_CA'])
    const page = isPreviewEnabled ? pages[0] : filterPublished(pages)[0]
    if (!page) return {}

    fields = page.fields
    pageType.value = page.sys?.contentType?.sys?.id
    const seo = getSeoPanelData(fields.seoPanel, includes)
    const panels = fields.panels
      ?.filter((panel: any) => panel.sys.contentType) // remove invalid items
      ?.map((panel: any) => ({
        ...panel.fields,
        id: panel.sys.id,
        componentName: panel.sys.contentType?.sys.id,
      }))
    const supportingData = fields.supportingData
    return {
      pageType,
      seo,
      panels,
      supportingData,
    }
  } else if (itemType === 'productListingPage') {
    pageType.value = itemType
    const seo = getSeoPanelData(fields.seoPanel, includes)
    const supportingData = fields.supportingData
    const categoryId = fields.categoryId
    const quickLinks = fields.quickLinks
    const inlineListTeaserCard1 = fields.inlineListTeaserCard1
    const inlineListTeaserCard2 = fields.inlineListTeaserCard2
    const inlineListTeaserCard3 = fields.inlineListTeaserCard3
    const preListContent = fields.preListContent
      ?.filter((panel: any) => panel.sys.contentType) // remove invalid items
      ?.map((panel: any) => ({
        ...panel.fields,
        id: panel.sys.id,
        componentName: panel.sys.contentType?.sys.id,
      }))
    const postListContent = fields.postListContent
      ?.filter((panel: any) => panel.sys.contentType) // remove invalid items
      ?.map((panel: any) => ({
        ...panel.fields,
        id: panel.sys.id,
        componentName: panel.sys.contentType?.sys.id,
      }))

    return {
      pageType,
      seo,
      categoryId,
      preListContent,
      postListContent,
      quickLinks,
      inlineListTeaserCard1,
      inlineListTeaserCard2,
      inlineListTeaserCard3,
      supportingData,
    }
  }
  throw new Error('Page Not Found')
}

usePageContentType().setContentType(mappedContentType)

onMounted(() => {
  onMountedFirstLoadHandler()
})

function updateBreadcrumbs(links: { text: string; url: string }[]) {
  breadcrumbs.value = links
}
</script>

<template>
  <BreadCrumbs
    v-if="data?.supportingData?.home?.showBreadCrumbs?.value"
    :path="slug"
    :links="breadcrumbs"
    class="container"
  />
  <ContentPage v-if="data?.pageType?.value === 'contentPage'" v-bind="data" />
  <ProductListingPage
    v-else-if="data?.pageType?.value === 'productListingPage'"
    @update-breadcrumbs="updateBreadcrumbs"
    v-bind="data"
  />
  <NotFoundPage v-else />
</template>
